// @story MSG-SYS10
// A box for message words with details in them (design 30 Oct 2026: "Nobody
// types a variable"). The words are a normal text box, so typing, selecting,
// copying and undo work as everywhere; each detail shows as a chip by its
// plain name, [First name], drawn on a layer behind the text. "Insert
// detail" puts one where the cursor is; Backspace or Delete next to a chip
// removes the whole detail. What is saved is the same {{firstName}} as
// before, so every message written earlier still reads. A {{name}} typed by
// hand that is not a detail is flagged under the box.

import React, { useLayoutEffect, useMemo, useRef } from 'react';
import { Select } from '../../ds';
import { chipsIn, fromDisplay, toDisplay, unknownIn } from './details';

const text = {
  boxSizing: 'border-box', width: '100%', margin: 0, padding: '10px 14px',
  font: 'inherit', fontSize: 'var(--fs-body-md)', lineHeight: 1.6, letterSpacing: 'normal',
  whiteSpace: 'pre-wrap', overflowWrap: 'break-word', wordBreak: 'normal',
  border: '1px solid transparent', borderRadius: 'var(--radius-md)',
};

/* The dropdown: plain names, with a word on those not everyone has. */
export function InsertDetail({ details, onPick, disabled = false, id }) {
  const options = details.map((d) => ({
    value: d.key,
    label: `${d.label}${d.own ? ' (this communication)' : d.canBeEmpty ? ' (not everyone has one)' : ''}`,
  }));
  return (
    <div style={{ minWidth: 220 }} data-testid={id ? `${id}-insert` : undefined}>
      <Select value="" placeholder="Insert detail…" options={options} disabled={disabled} onChange={(k) => { if (k) onPick(k); }} />
    </div>
  );
}

export default function DetailField({
  id, label, value, onChange, details, multiline = true, rows = 4, placeholder, disabled = false, hint, actions,
}) {
  const box = useRef(null);
  const back = useRef(null);
  const caret = useRef(null);
  const at = useRef({ start: null, end: null });
  const display = useMemo(() => toDisplay(value, details), [value, details]);
  const chips = useMemo(() => chipsIn(display, details), [display, details]);
  const unknown = useMemo(() => unknownIn(value, details), [value, details]);

  // Keep the cursor where the person left it after a detail goes in or comes out.
  useLayoutEffect(() => {
    if (caret.current !== null && box.current) {
      box.current.focus();
      box.current.setSelectionRange(caret.current, caret.current);
      caret.current = null;
    }
    if (box.current && !multiline) {
      box.current.style.height = 'auto';
      box.current.style.height = `${box.current.scrollHeight}px`;
    }
  });

  const emit = (shownWords, cursor) => {
    if (cursor !== undefined) caret.current = cursor;
    onChange(fromDisplay(shownWords, details));
  };

  const remember = (e) => { at.current = { start: e.target.selectionStart, end: e.target.selectionEnd }; };

  const insert = (key) => {
    const d = details.find((x) => x.key === key);
    if (!d) return;
    const chip = `[${String(d.label).replace(/[[\]]/g, '').trim()}]`;
    const start = at.current.start ?? display.length;
    const end = at.current.end ?? start;
    const before = display.slice(0, start);
    const after = display.slice(end);
    // A space either side, unless there is one already or it is the edge of a line.
    const pre = before && !/[\s([]$/.test(before) ? ' ' : '';
    const post = after && !/^[\s.,;:!?)]/.test(after) ? ' ' : '';
    const next = `${before}${pre}${chip}${post}${after}`;
    const cursor = (before + pre + chip).length;
    at.current = { start: cursor, end: cursor };
    emit(next, cursor);
  };

  // Backspace / Delete beside a chip removes the whole detail, never half of its name.
  const onKeyDown = (e) => {
    if (!multiline && e.key === 'Enter') { e.preventDefault(); return; }
    const t = e.target;
    if (t.selectionStart !== t.selectionEnd) return;
    const pos = t.selectionStart;
    const hit = e.key === 'Backspace' ? chips.find((c) => c.end === pos || (c.start < pos && pos < c.end))
      : e.key === 'Delete' ? chips.find((c) => c.start === pos || (c.start < pos && pos < c.end)) : null;
    if (!hit) return;
    e.preventDefault();
    emit(display.slice(0, hit.start) + display.slice(hit.end), hit.start);
  };

  // The layer behind: the same words, transparent, with each detail drawn as a chip.
  const layer = [];
  let from = 0;
  chips.forEach((c, i) => {
    layer.push(display.slice(from, c.start));
    layer.push(
      <mark key={i} data-detail-chip style={{ color: 'transparent', background: 'var(--brand-primary-soft)', borderRadius: 6, boxShadow: '0 0 0 2px var(--brand-primary-soft)' }}>
        {display.slice(c.start, c.end)}
      </mark>,
    );
    from = c.end;
  });
  layer.push(`${display.slice(from)}\n`);

  return (
    <div style={{ display: 'grid', gap: 6 }}>
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <label htmlFor={id} style={{ fontSize: 'var(--fs-body-sm)', fontWeight: 500, color: 'var(--text-1)' }}>{label}</label>
        <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          {actions}
          <InsertDetail id={id} details={details} disabled={disabled} onPick={insert} />
        </div>
      </div>
      <div style={{ position: 'relative', background: disabled ? 'var(--surface-2)' : 'var(--surface-1)', borderRadius: 'var(--radius-md)' }}>
        <div ref={back} aria-hidden="true" style={{ ...text, position: 'absolute', inset: 0, overflow: 'hidden', color: 'transparent', pointerEvents: 'none' }}>{layer}</div>
        <textarea
          id={id}
          ref={box}
          rows={multiline ? rows : 1}
          value={display}
          disabled={disabled}
          placeholder={placeholder}
          spellCheck
          onChange={(e) => { remember(e); emit(e.target.value); }}
          onSelect={remember}
          onKeyUp={remember}
          onClick={remember}
          onKeyDown={onKeyDown}
          onScroll={(e) => { if (back.current) back.current.scrollTop = e.target.scrollTop; }}
          style={{
            ...text, position: 'relative', display: 'block', background: 'transparent', color: 'var(--text-1)',
            border: '1px solid var(--border-strong)', resize: multiline ? 'vertical' : 'none', overflow: multiline ? 'auto' : 'hidden',
          }}
        />
      </div>
      {hint && <span style={{ fontSize: 'var(--fs-caption)', color: 'var(--text-muted)' }}>{hint}</span>}
      {unknown.length > 0 && (
        <div role="alert" style={{ fontSize: 'var(--fs-body-sm)', color: 'var(--status-danger-text)' }}>
          {unknown.map((k) => `{{${k}}}`).join(', ')} {unknown.length === 1 ? 'is' : 'are'} not one of the details, so this cannot be sent. Remove {unknown.length === 1 ? 'it' : 'them'} and choose from "Insert detail".
        </div>
      )}
    </div>
  );
}
